import React from 'react';
import type { Expert } from '../../types/expert.types';
import './ExpertCard.styles.css';

interface ExpertCardProps {
  expert: Expert;
  onClick: () => void;
}

export const ExpertCard: React.FC<ExpertCardProps> = ({ expert, onClick }) => {
  // Calculate total upcoming appointments
  const totalUpcoming = expert.stats.next_week_appointments + expert.stats.week_after_appointments;
  
  // Calculate combined workload score (active dossiers + upcoming appointments)
  // Weight: 50% for active dossiers, 50% for upcoming appointments
  const workloadScore = (expert.stats.active_dossiers * 0.5) + (totalUpcoming * 0.5);
  
  const getWorkloadStatus = (score: number) => {
    if (score > 15) return { level: 'critical', color: '#dc2626', bg: '#fef2f2' };
    if (score > 10) return { level: 'high', color: '#ea580c', bg: '#fff7ed' };
    if (score > 5) return { level: 'medium', color: '#2563eb', bg: '#eff6ff' };
    return { level: 'low', color: '#16a34a', bg: '#f0fdf4' };
  };

  const workloadStatus = getWorkloadStatus(workloadScore);
  const workloadPercentage = Math.min((workloadScore / 20) * 100, 100);

  // Get initials from expert name
  const getInitials = (name: string) => {
    const parts = name.split(' ');
    return parts
      .map(part => part[0]?.toUpperCase())
      .filter(Boolean)
      .slice(0, 2)
      .join('');
  };

  // Format quality badge
  const formatQuality = (quality?: string) => {
    if (quality?.toLowerCase().includes('expert')) return 'Expert';
    if (quality?.toLowerCase().includes('certificateur')) return 'Certificateur';
    return quality || 'Expert';
  };

  return (
    <div className="admin-expert-card" onClick={onClick}>
      {/* Card Header with Avatar and Basic Info */}
      <div className="admin-expert-card__header">
        <div className="admin-expert-card__avatar" style={{ background: workloadStatus.bg }}>
          <span style={{ color: workloadStatus.color }}>{getInitials(expert.nom_traitant)}</span>
        </div>
        <div className="admin-expert-card__identity">
          <h3 className="admin-expert-card__name">{expert.nom_traitant}</h3>
          <div className="admin-expert-card__meta">
            <span className="admin-expert-card__badge">{formatQuality(expert.quality)}</span>
            {expert.email && (
              <span className="admin-expert-card__email" title={expert.email}>
                {expert.email}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Workload Visual Indicator */}
      <div className="admin-expert-card__workload">
        <div className="admin-expert-card__workload-header">
          <span className="admin-expert-card__workload-title">Charge de travail</span>
          <span 
            className="admin-expert-card__workload-badge" 
            style={{ color: workloadStatus.color, background: workloadStatus.bg }}
          >
            {workloadStatus.level === 'critical' && 'Critique'}
            {workloadStatus.level === 'high' && 'Élevée'}
            {workloadStatus.level === 'medium' && 'Normale'}
            {workloadStatus.level === 'low' && 'Faible'}
          </span>
        </div>
        <div className="admin-expert-card__workload-bar">
          <div 
            className="admin-expert-card__workload-fill" 
            style={{ 
              width: `${workloadPercentage}%`,
              background: workloadStatus.color
            }}
          />
        </div>
      </div>

      {/* Statistics Grid */}
      <div className="admin-expert-card__stats">
        <div className="admin-expert-card__stat admin-expert-card__stat--primary">
          <div className="admin-expert-card__stat-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 11H3v9h6v-9zm5-7H8v16h6V4zm5 3h-6v13h6V7z"/>
            </svg>
          </div>
          <div className="admin-expert-card__stat-content">
            <span className="admin-expert-card__stat-value">{expert.stats.active_dossiers}</span>
            <span className="admin-expert-card__stat-label">Dossiers actifs</span>
          </div>
        </div>

        <div className="admin-expert-card__stat">
          <div className="admin-expert-card__stat-icon admin-expert-card__stat-icon--calendar">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </div>
          <div className="admin-expert-card__stat-content">
            <span className="admin-expert-card__stat-value">{totalUpcoming}</span>
            <span className="admin-expert-card__stat-label">RDV à venir</span>
          </div>
        </div>
      </div>

      {/* Detailed Appointments Breakdown */}
      <div className="admin-expert-card__appointments">
        <div className="admin-expert-card__appointment">
          <span className="admin-expert-card__appointment-label">
            <span className="admin-expert-card__appointment-dot admin-expert-card__appointment-dot--next"></span>
            Semaine prochaine
          </span>
          <span className="admin-expert-card__appointment-value">
            {expert.stats.next_week_appointments}
          </span>
        </div>
        <div className="admin-expert-card__appointment">
          <span className="admin-expert-card__appointment-label">
            <span className="admin-expert-card__appointment-dot admin-expert-card__appointment-dot--after"></span>
            Dans 2 semaines
          </span>
          <span className="admin-expert-card__appointment-value">
            {expert.stats.week_after_appointments}
          </span>
        </div>
      </div>

      {/* Action Footer */}
      <div className="admin-expert-card__footer">
        <span className="admin-expert-card__action">
          Voir les dossiers
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </span>
      </div>
    </div>
  );
};